import React from 'react';
import { getCurrentUser, fetchAuthSession, signOut as amplifySignOut } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { AuthContext, AuthScreen } from './AuthContext';
import type { AuthSession } from './useAuthSession';
import { SignInScreen } from './screens/SignInScreen';
import { EmailScreen } from './screens/EmailScreen';
import { OtpScreen } from './screens/OtpScreen';
import './styles.css';

type AuthState = 'idle' | 'loading' | 'authenticated' | 'sign_in';

interface FsAuthenticatorProps {
  children: React.ReactNode;
}

export function FsAuthenticator({ children }: FsAuthenticatorProps) {
  const [authState, setAuthState] = React.useState<AuthState>('loading');
  const [screen, setScreen] = React.useState<AuthScreen>('sign_in');
  const [email, setEmail] = React.useState('');
  const [session, setSession] = React.useState<AuthSession>({
    isAuthenticated: false,
    userId: null,
    email: null,
    groups: [],
    signOut: async () => {},
  });

  const buildSession = React.useCallback(async (): Promise<AuthSession | null> => {
    try {
      const user = await getCurrentUser();
      const authSession = await fetchAuthSession();
      const idToken = authSession.tokens?.idToken;
      const payload = idToken?.payload ?? {};
      const groups = (payload['cognito:groups'] as string[] | undefined) ?? [];
      const emailVal = (payload['email'] as string | undefined) ?? null;

      return {
        isAuthenticated: true,
        userId: user.userId,
        email: emailVal,
        groups,
        signOut: async () => {
          await amplifySignOut();
          setAuthState('sign_in');
          setSession({
            isAuthenticated: false,
            userId: null,
            email: null,
            groups: [],
            signOut: async () => {},
          });
        },
      };
    } catch {
      return null;
    }
  }, []);

  const checkSession = React.useCallback(async () => {
    setAuthState('loading');
    const s = await buildSession();
    if (s) {
      setSession(s);
      setAuthState('authenticated');
    } else {
      setAuthState('sign_in');
    }
  }, [buildSession]);

  React.useEffect(() => {
    checkSession();

    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signedIn') {
        checkSession();
      } else if (payload.event === 'signedOut') {
        setAuthState('sign_in');
        setSession({
          isAuthenticated: false,
          userId: null,
          email: null,
          groups: [],
          signOut: async () => {},
        });
      }
    });

    return () => unsubscribe();
  }, [checkSession]);

  if (authState === 'loading') {
    return (
      <div className="fs-auth-fullscreen">
        <div className="fs-auth-spinner" role="status" aria-label="Loading" />
      </div>
    );
  }

  if (authState === 'authenticated') {
    return (
      <AuthContext.Provider value={{ screen, setScreen, email, setEmail, session }}>
        {children}
      </AuthContext.Provider>
    );
  }

  const onAuthenticated = async () => {
    const s = await buildSession();
    if (s) {
      setSession(s);
      setAuthState('authenticated');
    }
  };

  return (
    <AuthContext.Provider value={{ screen, setScreen, email, setEmail, session }}>
      {screen === 'sign_in' && <SignInScreen />}
      {screen === 'email_input' && <EmailScreen onAuthenticated={onAuthenticated} />}
      {screen === 'otp_input' && <OtpScreen onAuthenticated={onAuthenticated} />}
    </AuthContext.Provider>
  );
}
